import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Search, Send, Users, MessageSquare, Calendar, ArrowLeft, CheckCircle, XCircle, Clock
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function WhatsAppBroadcast({ session, onBack }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchGroupId, setSearchGroupId] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastResults, setBroadcastResults] = useState(null);
  const [delayMs, setDelayMs] = useState(2000); // Default 2 seconds

  const fetchGroups = useCallback(async () => {
    try {
      const response = await api.get(`/whatsapp/sessions/${session.sessionId}/groups`);
      const data = response.data;
      // Only show active (not muted) groups for broadcast
      setGroups(data.groups || []);
      
      // Load session config for default delay
      const sessionResponse = await api.get(`/whatsapp/sessions/${session.sessionId}`);
      const sessionData = sessionResponse.data;
      if (sessionData.session?.config?.broadcastDelay) {
        setDelayMs(sessionData.session.config.broadcastDelay);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  }, [session.sessionId]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error(t('bcast.toastSearchEmpty'));
      return;
    }

    try {
      setLoading(true);
      let url = `/whatsapp/search?q=${encodeURIComponent(searchQuery)}&sessionId=${encodeURIComponent(session.sessionId)}`;
      if (searchGroupId) {
        url += `&groupId=${encodeURIComponent(searchGroupId)}`;
      }

      const response = await api.get(url);
      const data = response.data;

      setSearchResults(data.messages || []);
      toast.success(t('bcast.toastFound', { count: data.total || 0 }));
    } catch (error) {
      console.error('Search error:', error);
      toast.error(t('bcast.toastSearchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedGroups.length === groups.length) {
      setSelectedGroups([]);
    } else {
      setSelectedGroups(groups.map(g => g.groupId));
    }
  };

  const handleToggleGroup = (groupId) => {
    if (selectedGroups.includes(groupId)) {
      setSelectedGroups(selectedGroups.filter(id => id !== groupId));
    } else {
      setSelectedGroups([...selectedGroups, groupId]);
    }
  };

  const handleBroadcast = async () => {
    if (selectedGroups.length === 0) {
      toast.error(t('bcast.toastSelectGroups'));
      return;
    }

    if (!broadcastMessage.trim()) {
      toast.error(t('bcast.toastWriteMessage'));
      return;
    }

    try {
      setBroadcasting(true);
      setBroadcastResults(null);

      const response = await api.post(`/whatsapp/sessions/${session.sessionId}/broadcast`, {
        group_ids: selectedGroups,
        message: broadcastMessage,
        delay_ms: delayMs,
      });
      const data = response.data;

      if (data.success) {
        setBroadcastResults(data);
        toast.success(t('bcast.toastSent', { count: data.sent }));
        if (data.failed > 0) {
          toast.warning(t('bcast.toastFailed', { count: data.failed }));
        }
      } else {
        toast.error(data.error || t('bcast.toastSendFailed'));
      }
    } catch (error) {
      console.error('Broadcast error:', error);
      toast.error(t('bcast.toastSendFailed'));
    } finally {
      setBroadcasting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="whatsapp-broadcast">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('bcast.title')}</h1>
          <p className="text-muted-foreground">{session.name} - {session.phoneNumber || t('bcast.noNumber')}</p>
        </div>
      </div>

      <Tabs defaultValue="broadcast" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="broadcast">
            <Send className="w-4 h-4 mr-2" />
            {t('bcast.tabBroadcast')}
          </TabsTrigger>
          <TabsTrigger value="search">
            <Search className="w-4 h-4 mr-2" />
            {t('bcast.tabSearch')}
          </TabsTrigger>
        </TabsList>

        {/* Broadcast Tab */}
        <TabsContent value="broadcast" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('bcast.broadcastTitle')}</CardTitle>
              <CardDescription>
                {t('bcast.broadcastSub')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Message Input */}
              <div className="space-y-2">
                <Label>{t('bcast.messageLabel')}</Label>
                <Textarea
                  placeholder={t('bcast.messagePh')}
                  rows={4}
                  value={broadcastMessage}
                  onChange={(e) => setBroadcastMessage(e.target.value)}
                />
              </div>

              {/* Group Selection */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{t('bcast.selectGroups', { sel: selectedGroups.length, total: groups.length })}</Label>
                  <Button variant="outline" size="sm" onClick={handleSelectAll}>
                    {selectedGroups.length === groups.length ? t('bcast.deselectAll') : t('bcast.selectAll')}
                  </Button>
                </div>

                <div className="border rounded-lg max-h-60 overflow-y-auto">
                  {groups.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      {t('bcast.noGroups')}
                    </div>
                  ) : (
                    groups.map((group) => (
                      <div
                        key={group.groupId}
                        className="flex items-center gap-3 p-3 border-b last:border-0 hover:bg-muted/50"
                      >
                        <Checkbox
                          checked={selectedGroups.includes(group.groupId)}
                          onCheckedChange={() => handleToggleGroup(group.groupId)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{group.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {t('bcast.participants', { count: group.participantCount || 0 })} • {t('bcast.messages', { count: group.messageCount || 0 })}
                          </p>
                        </div>
                        {group.isMuted === false && (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                            {t('bcast.activeBadge')}
                          </Badge>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Delay Configuration */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {t('bcast.delayLabel')}
                </Label>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    min={1}
                    max={30}
                    value={delayMs / 1000}
                    onChange={(e) => setDelayMs(Math.max(1000, Math.min(30000, parseInt(e.target.value) * 1000 || 2000)))}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">{t('bcast.delayHint')}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('bcast.delayNote', { count: selectedGroups.length, secs: Math.ceil((selectedGroups.length * delayMs) / 1000) })}
                </p>
              </div>

              {/* Send Button */}
              <Button 
                onClick={handleBroadcast} 
                disabled={broadcasting || selectedGroups.length === 0 || !broadcastMessage.trim()}
                className="w-full"
              >
                {broadcasting ? (
                  <>{t('bcast.sending')}</>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {t('bcast.sendTo', { count: selectedGroups.length })}
                  </>
                )}
              </Button>

              {/* Results */}
              {broadcastResults && (
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-4 mb-2">
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      <span>{t('bcast.sentCount', { count: broadcastResults.sent })}</span>
                    </div>
                    {broadcastResults.failed > 0 && (
                      <div className="flex items-center gap-2 text-red-600">
                        <XCircle className="w-4 h-4" />
                        <span>{t('bcast.failedCount', { count: broadcastResults.failed })}</span>
                      </div>
                    )}
                  </div>
                  {broadcastResults.results?.failed?.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      {t('bcast.failedLabel')}: {broadcastResults.results.failed.map(f => f.error).join(', ')}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Search Tab */}
        <TabsContent value="search" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('bcast.searchTitle')}</CardTitle>
              <CardDescription>
                {t('bcast.searchSub')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search Form */}
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    placeholder={t('bcast.searchPh')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <select
                  className="border rounded-md px-3"
                  value={searchGroupId}
                  onChange={(e) => setSearchGroupId(e.target.value)}
                >
                  <option value="">{t('bcast.allGroups')}</option>
                  {groups.map((group) => (
                    <option key={group.groupId} value={group.groupId}>
                      {group.name}
                    </option>
                  ))}
                </select>
                <Button onClick={handleSearch} disabled={loading}>
                  <Search className="w-4 h-4 mr-2" />
                  {t('bcast.searchBtn')}
                </Button>
              </div>

              {/* Results */}
              <div className="border rounded-lg">
                {loading ? (
                  <div className="p-8 text-center text-muted-foreground">
                    {t('bcast.searching')}
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>{t('bcast.noResults')}</p>
                    <p className="text-sm">{t('bcast.noResultsHint')}</p>
                  </div>
                ) : (
                  <div className="divide-y max-h-96 overflow-y-auto">
                    {searchResults.map((msg) => (
                      <div key={msg.messageId} className="p-3 hover:bg-muted/50">
                        <div className="flex items-center gap-2 mb-1">
                          {msg.groupName && (
                            <Badge variant="outline" className="text-xs">
                              <Users className="w-3 h-3 mr-1" />
                              {msg.groupName}
                            </Badge>
                          )}
                          {!msg.groupName && msg.directPhone && (
                            <Badge variant="outline" className="text-xs" data-testid={`direct-contact-badge-${msg.messageId}`}>
                              {msg.directPhone}
                            </Badge>
                          )}
                          <span className="text-xs text-muted-foreground">
                            <Calendar className="w-3 h-3 inline mr-1" />
                            {formatDate(msg.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm">{msg.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
